import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Warning, CheckCircle, Clock, X } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

/**
 * Full-width breakdown banner — appears at the top of every page for the
 * shift millwright (and managers/admins) whenever a Grindrod-accountable
 * breakdown is OPEN (not yet acknowledged). One-tap ACCEPT button.
 *
 * Polls every 15s; auto-hides as soon as it's acknowledged or closed.
 */
const ELIGIBLE = new Set(["shift_millwright", "manager", "admin", "plant_manager"]);

function fmtAt(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

function fmtSince(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    return `${fmtDuration(mins)} ago`;
}

export default function BreakdownBanner() {
    const { user } = useAuth();
    const eligible = user && ELIGIBLE.has(user.role);
    const isMillwright = user?.role === "shift_millwright";
    const [openItems, setOpenItems] = useState([]);
    const [dismissedIds, setDismissedIds] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!eligible) return;
        try {
            // Fetch BOTH open (needs accept) and ready (needs supervisor resume)
            const [openR, readyR] = await Promise.all([
                api.get("/millwright/breakdown-notifications", { params: { status_filter: "open", limit: 10 } }),
                api.get("/millwright/breakdown-notifications", { params: { status_filter: "ready", limit: 10 } }),
            ]);
            const merged = [...(openR.data || []), ...(readyR.data || [])];
            setOpenItems(merged);
        } catch (err) {
            console.warn("Breakdown banner load failed:", err?.message || err);
        }
    }, [eligible]);

    useEffect(() => {
        if (!eligible) return;
        load();
        const t = setInterval(load, 15_000);
        return () => clearInterval(t);
    }, [load, eligible]);

    const visible = useMemo(
        () => openItems.filter((i) => !dismissedIds.has(i.id)),
        [openItems, dismissedIds]
    );

    const accept = async (n) => {
        setBusy(true);
        try {
            await api.post(`/millwright/breakdown-notifications/${n.id}/acknowledge`, { note: "" });
            await load();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to accept");
        } finally {
            setBusy(false);
        }
    };

    const dismiss = (id) => {
        const next = new Set(dismissedIds);
        next.add(id);
        setDismissedIds(next);
    };

    if (!eligible || visible.length === 0) return null;

    const isReady = (n) => n.status === "ready";

    return (
        <div className="space-y-0" data-testid="breakdown-banner">
            {visible.map((n) => (
                <div
                    key={n.id}
                    className={`${isReady(n) ? "bg-[var(--grindrod-green)]" : "bg-[var(--grindrod-red)]"} text-white border-y-4 ${isReady(n) ? "border-white/70" : "border-yellow-300"} shadow-lg`}
                    data-testid={`breakdown-banner-row-${n.id}`}
                >
                    <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-start gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                        <div className={`${isReady(n) ? "bg-white text-[var(--grindrod-green)]" : "bg-yellow-300 text-[var(--grindrod-red)]"} rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shrink-0 ${isReady(n) ? "" : "animate-pulse"}`}>
                            {isReady(n) ? <CheckCircle size={22} weight="fill" /> : <Warning size={22} weight="fill" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className={`text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold ${isReady(n) ? "text-white/95" : "text-yellow-200"}`}>
                                {isReady(n) ? "VESSEL READY TO RESUME · MILLWRIGHT SIGNED OFF" : "GRINDROD BREAKDOWN · NEEDS MAINTENANCE"}
                            </div>
                            <div className="font-display font-black text-lg sm:text-2xl leading-tight mt-0.5 break-words" data-testid={`breakdown-banner-title-${n.id}`}>
                                {n.conveyor_name || "Conveyor"} · {n.vessel_name}
                            </div>
                            <div className="text-sm mt-1 text-white/95 break-words">
                                {isReady(n) ? (
                                    <>Fixed by <strong>{n.ready_by_name}</strong>{n.ready_note ? ` — "${n.ready_note}"` : ""}</>
                                ) : (
                                    <>
                                        <span className="font-semibold">{n.reason}</span>
                                        {n.description ? ` — ${n.description}` : ""}
                                    </>
                                )}
                            </div>
                            <div className="text-[11px] mt-1.5 text-white/85 flex items-center gap-3 flex-wrap">
                                <span>Raised by <strong>{n.raised_by_name}</strong></span>
                                <span>·</span>
                                <span className="tabular">{fmtAt(n.raised_at)}</span>
                                <span>·</span>
                                <span>{fmtSince(n.raised_at)}</span>
                                {n.eta_minutes != null && !isReady(n) && (
                                    <>
                                        <span>·</span>
                                        <span className="inline-flex items-center gap-1">
                                            <Clock size={11} weight="fill" /> ETA <span className="tabular font-semibold">{fmtDuration(n.eta_minutes)}</span>
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                            {isReady(n) ? (
                                user?.role === "supervisor" ? (
                                    <button
                                        onClick={async () => {
                                            if (!window.confirm("Resume vessel loading? Maintenance has signed off.")) return;
                                            try {
                                                await api.post(`/vessels/${n.vessel_id}/resume`);
                                                await load();
                                            } catch (err) {
                                                alert(err?.response?.data?.detail || "Failed to resume");
                                            }
                                        }}
                                        className="bg-white text-[var(--grindrod-green)] hover:bg-emerald-50 font-bold uppercase tracking-wide text-xs sm:text-sm px-4 sm:px-5 py-2.5 sm:py-3 rounded-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
                                        data-testid={`breakdown-banner-resume-${n.id}`}
                                    >
                                        <CheckCircle size={16} weight="fill" /> Resume vessel loading
                                    </button>
                                ) : (
                                    <span className="bg-white/15 text-white text-[10px] sm:text-xs uppercase font-bold px-3 py-2 rounded-sm">
                                        Awaiting supervisor resume
                                    </span>
                                )
                            ) : isMillwright ? (
                                <button
                                    onClick={() => accept(n)}
                                    disabled={busy}
                                    className="bg-white text-[var(--grindrod-red)] hover:bg-yellow-100 disabled:opacity-50 font-bold uppercase tracking-wide text-xs sm:text-sm px-4 sm:px-5 py-2.5 sm:py-3 rounded-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
                                    data-testid={`breakdown-banner-accept-${n.id}`}
                                >
                                    <CheckCircle size={16} weight="fill" /> Accept — Maintenance on the way
                                </button>
                            ) : (
                                <span className="bg-white/15 text-white text-[10px] sm:text-xs uppercase font-bold px-3 py-2 rounded-sm" data-testid={`breakdown-banner-awaiting-${n.id}`}>
                                    Awaiting millwright accept
                                </span>
                            )}
                            <button
                                onClick={() => dismiss(n.id)}
                                className="opacity-70 hover:opacity-100 p-1 rounded-sm"
                                title="Hide this alert (will return on refresh)"
                                data-testid={`breakdown-banner-dismiss-${n.id}`}
                                aria-label="Dismiss"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
